(() => {
  if (customElements.get('sw-shipping-progress')) return;
  const tiers = [{cap:1000,minimum:15000},{cap:1500,minimum:22500},{cap:2000,minimum:30000},{cap:3000,minimum:45000}];
  const shared = {nodes:new Set(),cart:null,destination:null,quote:null,status:'idle',version:0,controller:null,timer:null};
  const root = window.Shopify?.routes?.root || '/';
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency:'EUR'}).format(cents / 100);
  const signature = cart => JSON.stringify([cart.currency,cart.subtotal,cart.count,cart.hasDiscount,cart.keys,cart.quantities]);
  const refresh = () => shared.nodes.forEach(node => node.render());
  const normalise = cart => ({currency:cart.currency,subtotal:cart.total_price,count:cart.item_count,requiresShipping:cart.requires_shipping,hasDiscount:cart.total_discount > 0,keys:cart.items.map(item=>item.key),quantities:cart.items.map(item=>item.quantity)});
  function setCart(cart) {
    if (shared.cart && signature(shared.cart) === signature(cart)) return;
    shared.cart = cart;
    shared.quote = null;
    shared.status = 'idle';
    shared.controller?.abort();
    shared.version++;
    refresh();
    if (shared.destination && cart.count && cart.currency === 'EUR' && !cart.hasDiscount) estimate();
  }
  async function readCart() {
    try {
      const response = await fetch(`${root}cart.js`,{credentials:'same-origin',headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error('Cart unavailable');
      setCart(normalise(await response.json()));
    } catch {
      shared.quote = null;
      shared.status = 'failed';
      refresh();
    }
  }
  function scheduleCart() {
    shared.quote = null;
    shared.status = 'busy';
    shared.controller?.abort();
    shared.version++;
    refresh();
    clearTimeout(shared.timer);
    shared.timer = setTimeout(async()=>{
      const previous = shared.cart && signature(shared.cart);
      await readCart();
      if (shared.cart && previous === signature(shared.cart)) {
        shared.status = 'idle';
        refresh();
        if (shared.destination && shared.cart.currency === 'EUR' && !shared.cart.hasDiscount) estimate();
      }
    },400);
  }
  async function estimate() {
    if (!shared.cart || !shared.destination) return;
    shared.controller?.abort();
    const controller = new AbortController();
    shared.controller = controller;
    const version = ++shared.version;
    const cartSignature = signature(shared.cart);
    shared.quote = null;
    shared.status = 'busy';
    refresh();
    const params = new URLSearchParams();
    Object.entries(shared.destination).forEach(([key,value])=>params.set(`shipping_address[${key}]`,value));
    const options = {credentials:'same-origin',signal:controller.signal,headers:{Accept:'application/json'}};
    try {
      const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`,{...options,method:'POST'});
      if (!prepared.ok) throw new Error('Invalid delivery details');
      const validation = await prepared.json();
      if (validation && !Array.isArray(validation.shipping_rates)) throw new Error('Invalid delivery details');
      let result = validation?.shipping_rates ? validation : null;
      for (let attempt=0;result === null && attempt<12;attempt++) {
        const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`,options);
        if (!response.ok) throw new Error('Shipping unavailable');
        result = await response.json();
        if (result === null) await new Promise(resolve=>setTimeout(resolve,650));
      }
      if (controller.signal.aborted || version !== shared.version || cartSignature !== signature(shared.cart)) return;
      if (result === null) throw new Error('Shipping timed out');
      shared.quote = {signature:cartSignature,rates:Array.isArray(result.shipping_rates)?result.shipping_rates:[]};
      shared.status = 'ready';
    } catch (error) {
      if (controller.signal.aborted || version !== shared.version) return;
      shared.status = 'failed';
    }
    refresh();
  }
  class ShippingProgress extends HTMLElement {
    static assess(cart,quote,status) {
      if (!cart?.count) return {key:'empty'};
      if (!cart.requiresShipping) return {key:'no_shipping'};
      if (cart.currency !== 'EUR') return {key:'currency'};
      if (cart.hasDiscount) return {key:'discount'};
      if (status === 'busy') return {key:'busy'};
      if (status === 'failed') return {key:'failed'};
      if (!quote || quote.signature !== signature(cart)) return {key:'initial'};
      const rates = quote.rates.filter(rate=>rate.price !== null && rate.price !== '' && (!rate.currency || rate.currency === cart.currency) && Number.isFinite(Number(rate.price)) && Number(rate.price) >= 0);
      if (!rates.length) return {key:'unavailable'};
      const price = Math.min(...rates.map(rate=>Math.round(Number(rate.price)*100)));
      if (price === 0) return {key:'success',minimum:cart.subtotal,progress:1,quote:0};
      const tier = tiers.find(tier=>price <= tier.cap);
      if (!tier) return {key:'paid'};
      const remaining = Math.max(0,tier.minimum-cart.subtotal);
      return {key:remaining?'add':'success',minimum:tier.minimum,remaining,quote:price,progress:Math.min(1,cart.subtotal/tier.minimum)};
    }
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.texts = JSON.parse(this.querySelector('[data-translations]').textContent);
      this.country = this.querySelector('[data-field="country"]');
      this.province = this.querySelector('[data-field="province"]');
      this.zip = this.querySelector('[data-field="zip"]');
      this.button = this.querySelector('[data-check]');
      this.country.value = shared.destination?.country || this.dataset.defaultCountry || this.country.options[0]?.value || '';
      this.updateProvinces(shared.destination?.province);
      this.zip.value = shared.destination?.zip || '';
      this.addEventListener('input',event=>{
        if (!event.target.matches('[data-field]')) return;
        shared.controller?.abort();
        shared.version++;
        shared.quote = null;
        shared.destination = null;
        shared.status = 'idle';
        refresh();
      });
      this.country.addEventListener('change',()=>this.updateProvinces());
      this.button.addEventListener('click',()=>{
        shared.destination = {country:this.country.value,province:this.province.value || '',zip:this.zip.value.trim()};
        shared.nodes.forEach(node=>{
          if (node === this) return;
          node.country.value = this.country.value;
          node.updateProvinces(this.province.value);
          node.zip.value = this.zip.value;
        });
        estimate();
      });
      shared.nodes.add(this);
      setCart(JSON.parse(this.querySelector('[data-cart]').textContent));
      this.render();
    }
    disconnectedCallback() {
      shared.nodes.delete(this);
      this.initialised = false;
    }
    updateProvinces(selected='') {
      let provinces = [];
      try {provinces=JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]');} catch {}
      this.province.replaceChildren(...provinces.map(([value,label])=>new Option(label,value)));
      if (selected) this.province.value = selected;
      this.querySelector('[data-province-wrap]').hidden = !provinces.length;
    }
    render() {
      const state = ShippingProgress.assess(shared.cart,shared.quote,shared.status);
      this.dataset.state = state.key;
      let message = this.texts[state.key] || this.texts.initial;
      if (state.remaining) message = message.replace('__amount__',money(state.remaining));
      this.querySelector('[data-message]').textContent = message;
      const progress = this.querySelector('[data-progress]');
      progress.hidden = state.progress === undefined;
      if (!progress.hidden) {progress.max=state.minimum||1;progress.value=Math.min(shared.cart.subtotal,progress.max);}
      this.querySelector('[data-detail]').textContent = state.quote === undefined ? this.texts.destination_hint : this.texts.detail.replace('__quote__',money(state.quote)).replace('__target__',money(state.minimum));
      this.button.disabled = shared.status === 'busy';
      this.button.textContent = shared.status === 'busy' ? this.texts.busy : this.texts.calculate;
      this.setAttribute('aria-busy',String(shared.status === 'busy'));
    }
  }
  customElements.define('sw-shipping-progress',ShippingProgress);
  function bindCart(attempt=0) {
    const bus = window.themeCore?.EventBus;
    if (bus?.listen) {
      bus.listen('cart:updated',scheduleCart);
      bus.listen('cart:refresh',scheduleCart);
      bus.listen('cart:drawer:open',readCart);
    } else if (attempt<20) setTimeout(()=>bindCart(attempt+1),150);
  }
  bindCart();
  document.addEventListener('shopify:section:load',readCart);
  document.addEventListener('change',event=>{if(event.target.closest('.cart-items__form'))scheduleCart();});
})();
